import { useState } from "preact/hooks";
import Alert from "@mui/material/Alert";
import {
  Button,
  Box,
  Container,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  InputLabel,
  FormControl,
  OutlinedInput,
  FormHelperText,
  Paper,
  Tab,
  Tabs
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useDebounce } from "@uidotdev/usehooks";
import { register } from "./data/api-client";
import { attemptLogin, useAuthContext } from "./data/auth";
import { useCheckUsername } from "./data/queries";
import { AppRouter } from "./Router";
import { Wordmark } from "./components/Wordmark";

interface LoginFieldErrors {
  username?: string;
  password?: string;
  confirmPassword?: string;
}

const INPUT_DELAY = 250;

function PasswordField({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  autoComplete
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  error: boolean;
  helperText?: string;
  autoComplete: string;
}) {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <FormControl variant="outlined">
      <InputLabel error={!!error} htmlFor={id}>
        {label}
      </InputLabel>
      <OutlinedInput
        type={showPassword ? "text" : "password"}
        label={label}
        id={id}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        error={error}
        autoComplete={autoComplete}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        }
      />
      <FormHelperText component="p" error={error}>
        {helperText ?? "\u00A0"}
      </FormHelperText>
    </FormControl>
  );
}

// Login / registration page
function LoginRegister() {
  const [tab, setTab] = useState<"login" | "register">("login");
  const { setUser } = useAuthContext();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(""); // error alert at bottom
  const [touched, setTouched] = useState<{
    username?: boolean;
    password?: boolean;
    confirmPassword?: boolean;
  }>({});

  const [logInLoading, setLogInLoading] = useState(false);
  const logInDisabled = logInLoading || !username || !password;
  const doLogin = async () => {
    setLogInLoading(true);
    setError("");
    const success = await attemptLogin({ username, password }, setUser);
    setLogInLoading(false);
    if (!success) {
      setError("Invalid credentials");
    }
  };
  const usernameDebounced = useDebounce(username, INPUT_DELAY);
  const { data: usernameData, isLoading: usernameLoading } = useCheckUsername(
    usernameDebounced,
    tab === "register"
  );
  const usernameValid =
    !usernameLoading &&
    usernameData?.username === username &&
    usernameData?.available &&
    usernameData?.valid;
  const [regLoading, setRegLoading] = useState(false);
  const regDisabled =
    regLoading || !usernameValid || !password || password !== confirmPassword;
  const doRegister = async () => {
    const reg = { username, password };
    setRegLoading(true);
    setError("");
    const error = await register(reg);
    if (error) {
      setError(error);
      setRegLoading(false);
    } else {
      await attemptLogin(reg, setUser);
    }
  };

  const errors: LoginFieldErrors = {}; // field-specific errors
  if (!username && touched.username) {
    errors.username = "Username required";
  } else if (
    tab === "register" &&
    username &&
    !usernameLoading &&
    usernameData?.username === username
  ) {
    if (!usernameData.valid) {
      errors.username = "Only letters, numbers and hyphens";
    } else if (!usernameData.available) {
      errors.username = "Username unavailable";
    }
  }
  if (!password && touched.password) {
    errors.password = "Password required";
  }
  if (!confirmPassword && touched.confirmPassword) {
    errors.confirmPassword = "Confirm your password";
  } else if (confirmPassword && password !== confirmPassword) {
    errors.confirmPassword = "Passwords do not match";
  }

  return (
    <Container>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Paper sx={{ width: "100%", maxWidth: 400, p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Wordmark scale="huge" />
          </Box>
          <Tabs
            value={tab}
            onChange={(_e, value: "login" | "register") => setTab(value)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
          >
            <Tab label="Sign In" value="login" />
            <Tab label="Register" value="register" />
          </Tabs>
          <Stack
            component="form"
            spacing={2}
            onSubmit={(e) => {
              e.preventDefault();
              if (tab === "login") {
                doLogin();
              } else {
                doRegister();
              }
            }}
          >
            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.currentTarget.value)}
              onBlur={() => setTouched((t) => ({ ...t, username: true }))}
              error={!!errors.username}
              helperText={errors.username ?? "\u00A0"}
              autoComplete="username"
            />
            <PasswordField
              id="password"
              label="Password"
              value={password}
              onChange={setPassword}
              onBlur={() => setTouched((t) => ({ ...t, password: true }))}
              error={!!errors.password}
              helperText={errors.password}
              autoComplete={
                tab === "login" ? "current-password" : "new-password"
              }
            />
            {tab === "register" && (
              <PasswordField
                id="confirm-password"
                label="Confirm Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                onBlur={() =>
                  setTouched((t) => ({ ...t, confirmPassword: true }))
                }
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                autoComplete="new-password"
              />
            )}
            {tab === "login" ? (
              <Button
                disabled={logInDisabled}
                loading={logInLoading}
                variant="contained"
                type="submit"
              >
                Sign In
              </Button>
            ) : (
              <Button
                id="reg-button"
                disabled={regDisabled}
                loading={regLoading}
                variant="contained"
                color="secondary"
                type="submit"
              >
                Register
              </Button>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
}

export function LoginOrApp() {
  const { user } = useAuthContext();
  if (user == null) {
    return <LoginRegister />;
  }
  return <AppRouter />;
}
